import { KeyRound, Loader2 } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompleteNewPassword } from "~/api/authentication/authentication";
import { useGetPasswordRules } from "~/api/users/users";
import passwordBg from "~/assets/onboarding/password.svg";
import SmartPasswordInput from "~/components/smart-password-input";
import { storeAccessToken, storeRefreshToken, storeUsername } from "~/helpers";
import { useAuthStepImage } from "../hooks/useAuthStepImage";

export const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, username } = location.state || {};

  useAuthStepImage(passwordBg);

  const {
    mutate: completeNewPassword,
    isPending,
    isError,
  } = useCompleteNewPassword();

  const { data: passwordRules, isLoading: isLoadingPasswordRules } =
    useGetPasswordRules();

  const onSavePasswordClick = (password: string) => {
    completeNewPassword(
      { data: { newPassword: password, session, username } },
      {
        onSuccess: (data) => {
          if (data.AccessToken && data.RefreshToken) {
            storeAccessToken(data.AccessToken);
            storeRefreshToken(data.RefreshToken);
            storeUsername(username);
            navigate("/");
          }
        },
      }
    );
  };

  return (
    <div className="flex justify-center items-start pt-10">
      {isLoadingPasswordRules ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-[560px] w-full">
          <div className="flex flex-col gap-4 ">
            <KeyRound className="h-[32px] w-[32px] text-[#71717A]" />
            <h3 className="text-2xl font-semibold text-base-foreground pt-[16px]">
              <FormattedMessage
                id="changePasswordForm.title"
                defaultMessage="Create New Password"
              />
            </h3>
            <span className="text-sm text-muted-foreground">
              <FormattedMessage
                id="changePasswordForm.subtitle"
                defaultMessage="Change your temporary password to a personal and secure one."
              />
            </span>
          </div>
          <SmartPasswordInput
            onPasswordSubmit={onSavePasswordClick}
            activeRequirements={passwordRules?.rules || []}
            isPending={isPending}
          />
          {isError && (
            <p className="mt-4 text-red-600 text-sm text-center">
              <FormattedMessage
                id="changePasswordForm.error"
                defaultMessage="Failed to change password"
              />
            </p>
          )}
        </div>
      )}
    </div>
  );
};
